import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import api, { apiErr } from "../lib/api";
import { useLang } from "../i18n";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=user
  const { setLang } = useLang();

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (data.language) setLang(data.language);
      return data;
    } catch (e) {
      console.warn("Auth refresh: not signed in", e?.response?.status);
      setUser(false);
      return null;
    }
  }, [setLang]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const chosen = localStorage.getItem("gs_lang");
    const { data } = await api.post("/auth/login", { email, password });
    let u = data;
    // Honour a language the user picked in-app (e.g. on the auth screen) over the saved profile.
    if (chosen && chosen !== data.language) {
      try { const r = await api.patch("/users/me", { language: chosen }); u = r.data; }
      catch (e) { console.warn("Language sync on login failed", e); }
    }
    setUser(u);
    setLang(u.language || chosen || "en");
    return u;
  }, [setLang]);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.warn("Logout request failed", e); }
    setUser(false);
  }, []);

  const updateProfile = useCallback(async (payload) => {
    const { data } = await api.patch("/users/me", payload);
    setUser(data);
    if (data.language) setLang(data.language);
    return data;
  }, [setLang]);

  const value = useMemo(
    () => ({ user, setUser, login, register, logout, updateProfile, refresh, apiErr }),
    [user, login, register, logout, updateProfile, refresh]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
